// Búfer circular del stream (~2 s) y ráfagas X+1+Y por disparo (ADR 0004 §3).
// La fuente de fotogramas es una interfaz (`FrameSource`): en el navegador la implementa
// `VideoFrameSource` sobre el `<video>` del stream de `getUserMedia`; en las pruebas, una fuente
// sintética. Nunca hay otra vía de entrada: no se aceptan archivos ni imágenes subidas.
import type { BurstShape } from './burst.js';
import type { CaptureClock } from './clock.js';
import { dhashFromGray, grayResize, sha256Hex } from './crypto.js';
import type { CaptureFrame, CaptureFrameSettings } from './types.js';

/** Un fotograma tal y como sale del stream, antes de codificarlo. */
export interface SourceFrame {
  /** µs desde el origen de la sesión. */
  t_mono: number;
  /** ms Unix. */
  t_wall: number;
  settings: CaptureFrameSettings;
  /** 72 grises (9×8, media de área) para el dHash. */
  gray9x8: Uint8Array;
  /** 1024 grises (32×32) para ruido de sensor y calidad. */
  gray32: Uint8Array;
  /** Media RGB del fotograma (0..255). */
  meanRgb: [number, number, number];
  /** Luminancia media BT.601 (0..255). */
  luma: number;
  /** Codifica el fotograma (JPEG calidad 0,85 en el navegador). */
  encode(): Promise<Uint8Array>;
  /** Libera recursos (p. ej. `ImageBitmap.close()`). */
  release?(): void;
}

/** Fuente de fotogramas en directo. */
export interface FrameSource {
  start(onFrame: (frame: SourceFrame) => void): void;
  stop(): void;
}

/** Fotograma de una ráfaga, ya codificado y con su entrada del bundle. */
export interface EncodedFrame {
  meta: CaptureFrame;
  bytes: Uint8Array;
  source: SourceFrame;
}

/** Ráfaga de un disparo: X anteriores, el elegido (posición X) e Y posteriores. */
export interface Burst {
  chosen_frame: number;
  frames: EncodedFrame[];
}

export interface FrameRingOptions {
  /** Duración del búfer (ms). Por defecto 2000. */
  durationMs?: number;
  /** Tope de fotogramas en memoria. Por defecto 120. */
  maxFrames?: number;
  /** Espera máxima de un fotograma antes de dar el stream por muerto (ms). Por defecto 5000. */
  frameTimeoutMs?: number;
}

/** Fotogramas mínimos que se conservan aunque superen la duración (X máximo + elegido). */
const MIN_KEEP = 9;

/**
 * Búfer circular de los últimos ~2 s del stream.
 *
 * ```ts
 * const ring = new FrameRing(source); ring.start();
 * const burst = await ring.shoot(await deriveBurst(seed, i)); // X+1+Y, chosen_frame = X
 * ```
 */
export class FrameRing {
  private readonly buf: SourceFrame[] = [];
  private readonly pinned = new Map<SourceFrame, number>();
  private readonly listeners = new Set<(f: SourceFrame) => void>();
  private readonly durationUs: number;
  private readonly maxFrames: number;
  private readonly timeoutMs: number;
  private running = false;
  /** `t_mono` del último fotograma usado en una ráfaga: ningún fotograma entra en dos disparos. */
  private usedUpTo = -1;

  constructor(
    private readonly source: FrameSource,
    opts: FrameRingOptions = {},
  ) {
    this.durationUs = (opts.durationMs ?? 2000) * 1000;
    this.maxFrames = Math.max(MIN_KEEP, opts.maxFrames ?? 120);
    this.timeoutMs = opts.frameTimeoutMs ?? 5000;
  }

  /** Empieza a llenar el búfer. */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.source.start((f) => this.push(f));
  }

  /** Para la fuente y libera todo lo que no esté en una ráfaga en curso. */
  stop(): void {
    if (!this.running) return;
    this.running = false;
    this.source.stop();
    for (const f of this.buf.splice(0)) if (!this.pinned.has(f)) f.release?.();
  }

  /** Fotogramas en el búfer, del más antiguo al más reciente. */
  frames(): readonly SourceFrame[] {
    return this.buf;
  }

  /** Último fotograma recibido. */
  latest(): SourceFrame | undefined {
    return this.buf[this.buf.length - 1];
  }

  /** Suscripción a cada fotograma nuevo; devuelve la función para darse de baja. */
  onFrame(listener: (f: SourceFrame) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Espera los próximos `n` fotogramas (por defecto 1). */
  nextFrames(n = 1): Promise<SourceFrame[]> {
    return new Promise((resolve, reject) => {
      const got: SourceFrame[] = [];
      let timer = setTimeout(fail, this.timeoutMs);
      const off = this.onFrame((f) => {
        got.push(f);
        clearTimeout(timer);
        if (got.length >= n) {
          off();
          resolve(got);
        } else timer = setTimeout(fail, this.timeoutMs);
      });
      function fail() {
        off();
        reject(new Error('frame_timeout'));
      }
    });
  }

  /** Fotograma del búfer más cercano a `t_mono` (µs). */
  nearest(t_mono: number): SourceFrame | undefined {
    let best: SourceFrame | undefined;
    for (const f of this.buf)
      if (!best || Math.abs(f.t_mono - t_mono) < Math.abs(best.t_mono - t_mono)) best = f;
    return best;
  }

  /**
   * Dispara: toma el último fotograma como elegido, los X anteriores del búfer y espera los Y
   * siguientes; codifica los X+1+Y y calcula su SHA-256 y su dHash. Nunca reutiliza fotogramas
   * de una ráfaga anterior (si hace falta, espera a que lleguen X+1 nuevos).
   */
  async shoot(shape: BurstShape): Promise<Burst> {
    if (!this.running) throw new Error('ring_not_running');
    // Solo fotogramas posteriores a la ráfaga anterior (un mismo hash no puede repetirse).
    const fresh = () => this.buf.filter((f) => f.t_mono > this.usedUpTo);
    while (fresh().length < shape.pre + 1) await this.nextFrames(1);
    const before = fresh().slice(-(shape.pre + 1));
    this.usedUpTo = before[before.length - 1]!.t_mono;
    before.forEach((f) => this.pin(f));
    let after: SourceFrame[] = [];
    try {
      after = shape.post > 0 ? await this.nextFrames(shape.post) : [];
      after.forEach((f) => this.pin(f));
      if (after.length) this.usedUpTo = after[after.length - 1]!.t_mono;
      const all = [...before, ...after];
      const frames = await Promise.all(
        all.map(async (source, idx): Promise<EncodedFrame> => {
          const bytes = await source.encode();
          return {
            bytes,
            source,
            meta: {
              idx,
              t_mono: source.t_mono,
              t_wall: source.t_wall,
              sha256: await sha256Hex(bytes),
              phash: dhashFromGray(source.gray9x8),
              settings: { ...source.settings },
            },
          };
        }),
      );
      return { chosen_frame: shape.pre, frames };
    } finally {
      [...before, ...after].forEach((f) => this.unpin(f));
    }
  }

  private push(f: SourceFrame): void {
    const last = this.latest();
    if (last && f.t_mono <= last.t_mono) {
      // Fotograma repetido o reloj hacia atrás: no entra (las ráfagas exigen t_mono creciente).
      f.release?.();
      return;
    }
    this.buf.push(f);
    while (
      this.buf.length > MIN_KEEP &&
      (this.buf.length > this.maxFrames || f.t_mono - this.buf[0]!.t_mono > this.durationUs)
    ) {
      const old = this.buf.shift()!;
      if (!this.pinned.has(old)) old.release?.();
    }
    for (const l of [...this.listeners]) l(f);
  }

  private pin(f: SourceFrame): void {
    this.pinned.set(f, (this.pinned.get(f) ?? 0) + 1);
  }

  private unpin(f: SourceFrame): void {
    const n = (this.pinned.get(f) ?? 1) - 1;
    if (n > 0) {
      this.pinned.set(f, n);
      return;
    }
    this.pinned.delete(f);
    if (!this.buf.includes(f)) f.release?.();
  }
}

/** Estadísticos de un fotograma a partir de sus píxeles RGBA reducidos. */
export function frameStats(
  rgba: ArrayLike<number>,
  width: number,
  height: number,
): Pick<SourceFrame, 'gray9x8' | 'gray32' | 'meanRgb' | 'luma'> {
  let r = 0;
  let g = 0;
  let b = 0;
  const n = width * height;
  for (let p = 0; p < n * 4; p += 4) {
    r += rgba[p]!;
    g += rgba[p + 1]!;
    b += rgba[p + 2]!;
  }
  const meanRgb: [number, number, number] = [
    Math.round(r / n),
    Math.round(g / n),
    Math.round(b / n),
  ];
  return {
    gray9x8: grayResize(rgba, width, height, 9, 8),
    gray32: grayResize(rgba, width, height, 32, 32),
    meanRgb,
    luma: Math.round((0.299 * r + 0.587 * g + 0.114 * b) / n),
  };
}

/** Lo mínimo de un `MediaStreamTrack` que necesita la fuente de vídeo. */
export interface TrackLike {
  getSettings(): MediaTrackSettings;
}

/** Ancho y alto del muestreo reducido por fotograma (proporción 9:8 ampliada). */
const SAMPLE_W = 72;
const SAMPLE_H = 64;

/** Ajustes del fotograma a partir de los del track (incluye exposición e ISO si existen). */
export function frameSettings(
  s: MediaTrackSettings & { exposureTime?: number; iso?: number },
  fallback: { width: number; height: number },
): CaptureFrameSettings {
  const out: CaptureFrameSettings = {
    width: Math.round(s.width ?? fallback.width),
    height: Math.round(s.height ?? fallback.height),
    frame_rate: s.frameRate && s.frameRate > 0 ? Math.round(s.frameRate * 1000) / 1000 : 30,
  };
  if (typeof s.exposureTime === 'number' && s.exposureTime >= 0) out.exposure_time = s.exposureTime;
  if (typeof s.iso === 'number' && s.iso >= 0) out.iso = s.iso;
  return out;
}

type VideoWithRvfc = HTMLVideoElement & {
  requestVideoFrameCallback?: (
    cb: (now: number, meta: { captureTime?: number; mediaTime?: number }) => void,
  ) => number;
  cancelVideoFrameCallback?: (id: number) => void;
};

/**
 * Fuente de fotogramas del navegador: lee el `<video>` que reproduce el stream de `getUserMedia`
 * con `requestVideoFrameCallback` (cada fotograma presentado una vez) o, si no existe,
 * `requestAnimationFrame` saltando los fotogramas repetidos. Guarda un `ImageBitmap` por
 * fotograma y solo codifica a JPEG (0,85) los de las ráfagas.
 */
export class VideoFrameSource implements FrameSource {
  private handle = 0;
  private active = false;
  private lastMediaTime = -1;
  private settingsAt = -Infinity;
  private settings: CaptureFrameSettings = { width: 1, height: 1, frame_rate: 30 };
  private readonly sample: OffscreenCanvas | HTMLCanvasElement;

  constructor(
    private readonly video: HTMLVideoElement,
    private readonly track: TrackLike,
    private readonly clock: CaptureClock,
  ) {
    this.sample = makeCanvas(SAMPLE_W, SAMPLE_H);
  }

  start(onFrame: (frame: SourceFrame) => void): void {
    this.active = true;
    const v = this.video as VideoWithRvfc;
    const useRvfc = typeof v.requestVideoFrameCallback === 'function';
    const tick = (now: number, meta?: { captureTime?: number; mediaTime?: number }) => {
      if (!this.active) return;
      const mediaTime = meta?.mediaTime ?? this.video.currentTime;
      if (mediaTime !== this.lastMediaTime && this.video.videoWidth > 0) {
        this.lastMediaTime = mediaTime;
        void this.grab(meta?.captureTime ?? now).then((f) => {
          if (f && this.active) onFrame(f);
          else f?.release?.();
        });
      }
      this.handle = useRvfc ? v.requestVideoFrameCallback!(tick) : requestAnimationFrame(tick);
    };
    this.handle = useRvfc ? v.requestVideoFrameCallback!(tick) : requestAnimationFrame(tick);
  }

  stop(): void {
    this.active = false;
    const v = this.video as VideoWithRvfc;
    if (typeof v.cancelVideoFrameCallback === 'function') v.cancelVideoFrameCallback(this.handle);
    else cancelAnimationFrame(this.handle);
  }

  private async grab(perfMs: number): Promise<SourceFrame | null> {
    const t_mono = this.clock.fromPerf(perfMs);
    const t_wall = this.clock.wall();
    if (perfMs - this.settingsAt > 500) {
      this.settingsAt = perfMs;
      this.settings = frameSettings(this.track.getSettings(), {
        width: this.video.videoWidth,
        height: this.video.videoHeight,
      });
    }
    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(this.video);
    } catch {
      return null;
    }
    const ctx = this.sample.getContext('2d', { willReadFrequently: true }) as
      CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
    if (!ctx) {
      bitmap.close();
      return null;
    }
    ctx.drawImage(bitmap, 0, 0, SAMPLE_W, SAMPLE_H);
    const px = ctx.getImageData(0, 0, SAMPLE_W, SAMPLE_H).data;
    const settings = { ...this.settings };
    return {
      t_mono,
      t_wall,
      settings,
      ...frameStats(px, SAMPLE_W, SAMPLE_H),
      encode: () => encodeJpeg(bitmap),
      release: () => bitmap.close(),
    };
  }
}

function makeCanvas(w: number, h: number): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

/** JPEG calidad 0,85 de un `ImageBitmap`. */
async function encodeJpeg(bitmap: ImageBitmap): Promise<Uint8Array> {
  const canvas = makeCanvas(bitmap.width, bitmap.height);
  const ctx = canvas.getContext('2d') as
    CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  if (!ctx) throw new Error('canvas_unavailable');
  ctx.drawImage(bitmap, 0, 0);
  const blob =
    'convertToBlob' in canvas
      ? await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.85 })
      : await new Promise<Blob>((resolve, reject) =>
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('jpeg_encode_failed'))),
            'image/jpeg',
            0.85,
          ),
        );
  return new Uint8Array(await blob.arrayBuffer());
}
